import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  SOCIAL_DOCK_DESKTOP_MEDIA_QUERY,
  shouldRenderSocialChatDock,
} from '@/social/chat-dock-visibility'
import { useSocialOverview, useSocialWorkspace } from '@/social/social-provider'
import type { SocialOverview } from '@/social/types'
import { useSocialText } from '@/social/use-social-text'
import { ExternalLink, Loader2, MessageCircle, Users, X } from 'lucide-react'
import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useMediaQuery } from 'usehooks-ts'

import { ConversationPanel } from './conversation-panel'
import { SocialPeoplePanel } from './social-people-panel'

export function SocialChatDock() {
  const text = useSocialText()
  const location = useLocation()
  const navigate = useNavigate()
  const workspace = useSocialWorkspace()
  const isDesktopViewport = useMediaQuery(SOCIAL_DOCK_DESKTOP_MEDIA_QUERY)
  const dockVisible = shouldRenderSocialChatDock({
    workspaceAvailable: workspace.available,
    isDesktopViewport,
    pathname: location.pathname,
    search: location.search,
  })
  const overviewQuery = useSocialOverview(dockVisible)
  const overview = overviewQuery.data as SocialOverview | undefined
  const activeConnection
    = overview?.administrator?.connectionId === workspace.activeConnectionId
      ? overview.administrator
      : (overview?.administratorConversations.find(
          conversation =>
            conversation.connectionId === workspace.activeConnectionId,
        )
        ?? overview?.friends.find(
          friend => friend.connectionId === workspace.activeConnectionId,
        )
        ?? null)
  const unread
    = (overview?.counts.unread ?? 0) + (overview?.counts.incoming ?? 0)

  useEffect(() => {
    if (
      !overviewQuery.isPending
      && !overviewQuery.isFetching
      && overview
      && workspace.activeConnectionId
      && !activeConnection
    ) {
      workspace.openConnection(null)
    }
  }, [
    activeConnection,
    overview,
    overviewQuery.isFetching,
    overviewQuery.isPending,
    workspace,
  ])

  if (!dockVisible)
    return null

  if (workspace.dockMode === 'minimized') {
    return (
      <Button
        type="button"
        size="icon"
        className="fixed bottom-6 right-6 z-[70] size-12 rounded-full shadow-xl"
        onClick={() => workspace.setDockMode('expanded')}
        title={text('social.actions.expand', 'Open messages')}
        data-testid="social-chat-dock-button"
      >
        <MessageCircle className="size-5" />
        {unread > 0 && (
          <span className="absolute -right-1 -top-1 flex min-h-5 min-w-5 items-center justify-center rounded-full border-2 border-background bg-destructive px-1 text-[10px] font-semibold text-destructive-foreground">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
        <span className="sr-only">
          {text('social.actions.expand', 'Open messages')}
        </span>
      </Button>
    )
  }

  const selectConnection = (connectionId: string) => {
    workspace.openConnection(connectionId)
  }

  return (
    <div
      className="fixed bottom-6 right-6 z-[70] flex h-[min(600px,calc(100dvh-7rem))] w-[390px] overflow-hidden rounded-2xl border bg-background shadow-2xl"
      data-testid="social-chat-dock"
    >
      {overviewQuery.isPending ? (
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      ) : overviewQuery.isError || !overview ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
          <p className="text-sm text-destructive">
            {text('social.error', 'Something went wrong.')}
          </p>
          <Button
            size="sm"
            variant="outline"
            onClick={() => overviewQuery.refetch()}
          >
            {text('social.retry', 'Try again')}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => workspace.setDockMode('minimized')}
          >
            {text('social.actions.close', 'Close')}
          </Button>
        </div>
      ) : activeConnection ? (
        <ConversationPanel
          key={activeConnection.connectionId}
          connectionId={activeConnection.connectionId}
          friend={activeConnection.user}
          canMuteNotifications={activeConnection.canMuteNotifications}
          notificationsMuted={activeConnection.notificationsMuted}
          currentUserId={overview.profile.id}
          onBack={() => workspace.openConnection(null)}
          onClose={() => workspace.setDockMode('minimized')}
        />
      ) : (
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <DockHomeHeader
            unread={unread}
            onClose={() => workspace.setDockMode('minimized')}
            onDashboard={() => navigate('/dashboard?tab=social')}
          />
          <SocialPeoplePanel
            overview={overview}
            activeConnectionId={null}
            onSelectConnection={selectConnection}
            onOpenProfile={() => {
              workspace.setDockMode('minimized')
              navigate('/dashboard?tab=profile')
            }}
            className="flex-1"
          />
        </div>
      )}
    </div>
  )
}

function DockHomeHeader({
  unread,
  onClose,
  onDashboard,
}: {
  unread: number
  onClose: () => void
  onDashboard: () => void
}) {
  const text = useSocialText()
  return (
    <header className="flex h-14 shrink-0 items-center gap-2 border-b px-3">
      <span className="flex size-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
        <Users className="size-4" />
      </span>
      <h2 className="min-w-0 flex-1 truncate text-sm font-semibold">
        {text('social.title', 'Friends & messages')}
      </h2>
      {unread > 0 && (
        <Badge className="h-5 min-w-5 px-1 text-[10px]">
          {unread > 99 ? '99+' : unread}
        </Badge>
      )}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="size-8"
        onClick={onDashboard}
        title={text('social.actions.openDashboard', 'Open in Dashboard')}
      >
        <ExternalLink className="size-4" />
        <span className="sr-only">
          {text('social.actions.openDashboard', 'Open in Dashboard')}
        </span>
      </Button>
      {}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="size-8"
        onClick={onClose}
        title={text('social.actions.close', 'Close')}
        data-testid="social-chat-dock-close"
      >
        <X className="size-4" />
        <span className="sr-only">{text('social.actions.close', 'Close')}</span>
      </Button>
    </header>
  )
}
